import React from 'react';
import { X } from 'lucide-react';

interface OnboardingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onStartTour: () => void;
    userCredits: number;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({
    isOpen,
    onClose,
    onStartTour,
    userCredits
}) => {
    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed' as const,
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(12px)',
        },
        card: {
            background: 'rgba(18, 18, 18, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '20px',
            padding: '2.5rem',
            maxWidth: '560px',
            width: '100%',
            position: 'relative' as const,
        },
        closeBtn: {
            position: 'absolute' as const,
            top: '1rem',
            right: '1rem',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.06)',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(255, 255, 255, 0.4)',
            transition: 'all 0.2s',
        },
        title: {
            fontSize: '1.8rem',
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '-0.02em',
            marginBottom: '0.25rem',
        },
        titleItalic: {
            fontStyle: 'italic' as const,
            fontWeight: 300,
        },
        subtitle: {
            fontSize: '0.85rem',
            color: 'rgba(255, 255, 255, 0.4)',
            lineHeight: 1.6,
        },
        creditsBox: {
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '14px',
            padding: '1.25rem',
            margin: '1.5rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
        },
        creditsNum: {
            fontSize: '2rem',
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '-0.03em',
        },
        creditsLabel: {
            fontSize: '0.7rem',
            color: 'rgba(255, 255, 255, 0.35)',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.1em',
            fontWeight: 600,
        },
        costGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '0.75rem',
            margin: '1rem 0 1.5rem',
        },
        costItem: {
            textAlign: 'center' as const,
            padding: '0.75rem',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.06)',
        },
        costValue: {
            fontSize: '0.9rem',
            fontWeight: 700,
            color: '#fff',
            marginBottom: '0.25rem',
        },
        costLabel: {
            fontSize: '0.65rem',
            color: 'rgba(255, 255, 255, 0.35)',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.06em',
        },
        stepsTitle: {
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.35)',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.12em',
            marginBottom: '1rem',
        },
        step: {
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-start',
            marginBottom: '0.75rem',
        },
        stepNum: {
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.7rem',
            fontWeight: 700,
            flexShrink: 0,
        },
        stepTitle: {
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#fff',
            marginBottom: '0.15rem',
        },
        stepDesc: {
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.4)',
            lineHeight: 1.5,
        },
        ctaBtn: {
            width: '100%',
            padding: '0.85rem',
            borderRadius: '100px',
            background: '#fff',
            color: '#000',
            border: 'none',
            fontSize: '0.85rem',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.2s',
            letterSpacing: '0.02em',
            marginTop: '1.5rem',
        },
        skipBtn: {
            width: '100%',
            padding: '0.65rem',
            borderRadius: '100px',
            background: 'transparent',
            color: 'rgba(255, 255, 255, 0.35)',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            cursor: 'pointer',
            marginTop: '0.5rem',
        },
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.card}>
                <button onClick={onClose} style={styles.closeBtn}>
                    <X size={16} />
                </button>

                {/* Header */}
                <h2 style={styles.title}>
                    Bem-vindo ao <span style={styles.titleItalic}>Hiva</span>
                </h2>
                <p style={styles.subtitle}>
                    Você ganhou créditos grátis para testar. Gere suas primeiras fotos editoriais agora.
                </p>

                {/* Credits */}
                <div style={styles.creditsBox}>
                    <div>
                        <p style={styles.creditsNum}>{userCredits}</p>
                        <p style={styles.creditsLabel}>Créditos Grátis</p>
                    </div>
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                            = {userCredits} fotos editoriais
                        </p>
                    </div>
                </div>

                {/* Cost Grid */}
                <div style={styles.costGrid}>
                    <div style={styles.costItem}>
                        <p style={styles.costValue}>1 crédito</p>
                        <p style={styles.costLabel}>Foto 2K</p>
                    </div>
                    <div style={styles.costItem}>
                        <p style={styles.costValue}>10 créditos</p>
                        <p style={styles.costLabel}>Upscale 4K</p>
                    </div>
                    <div style={styles.costItem}>
                        <p style={styles.costValue}>5 créditos</p>
                        <p style={styles.costLabel}>Vídeo</p>
                    </div>
                </div>

                {/* Steps */}
                <p style={styles.stepsTitle}>Como usar</p>
                {[
                    { n: '01', title: 'Faça upload', desc: 'Foto da peça — do celular, flat lay ou cabide' },
                    { n: '02', title: 'Descreva a cena', desc: 'Ou use um template pronto da biblioteca' },
                    { n: '03', title: 'Clique em Gerar', desc: 'Em 30 segundos sua foto editorial está pronta' },
                ].map((s, i) => (
                    <div key={i} style={styles.step}>
                        <div style={styles.stepNum}>{s.n}</div>
                        <div>
                            <p style={styles.stepTitle}>{s.title}</p>
                            <p style={styles.stepDesc}>{s.desc}</p>
                        </div>
                    </div>
                ))}

                {/* CTA */}
                <button onClick={onStartTour} style={styles.ctaBtn}>
                    Começar a Criar
                </button>
                <button onClick={onClose} style={styles.skipBtn}>
                    Pular Tutorial
                </button>
            </div>
        </div>
    );
};
